import { motion } from "framer-motion";
import { Wallet, Coins, ArrowLeftRight, Rocket } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/lib/constants";

const STEP_ICONS = [Wallet, Coins, ArrowLeftRight, Rocket];

export default function HowToBuy() {
  const { t } = useTranslation();
  const steps = t("howtobuy.steps", { returnObjects: true }) || [];

  return (
    <section id="how-to-buy" data-testid="how-to-buy-section" className="relative py-24 md:py-32 bg-black/40 overflow-hidden">
      <div className="absolute inset-0 -z-10 opacity-30">
        <img src={IMAGES.sectionBg} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/70" />
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <p className="font-mono-alt text-[10px] tracking-[0.3em] text-secondary">{t("howtobuy.section_label")}</p>
          <h2 className="mt-3 font-display font-black tracking-tighter text-5xl md:text-6xl leading-[0.95]">
            {t("howtobuy.title_l1")}
            <br />
            <span className="text-primary neon-glow-primary">{t("howtobuy.title_l2")}</span>
          </h2>
        </motion.div>

        <div className="mt-14 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {steps.map((s, i) => {
            const Icon = STEP_ICONS[i] || Wallet;
            const n = String(i + 1).padStart(2, "0");
            return (
              <motion.div
                key={n}
                data-testid={`step-card-${n}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="relative glass rounded-sm border border-primary/25 p-6 hover:border-primary/60 transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <span className="font-display font-black text-4xl text-primary/30 group-hover:text-primary/60 transition-colors">{n}</span>
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-sm border border-primary/40 bg-primary/10 text-primary">
                    <Icon className="h-5 w-5" />
                  </span>
                </div>
                <h3 className="mt-6 font-display font-bold text-lg tracking-tight">{s.title}</h3>
                <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
              </motion.div>
            );
          })}
        </div>

        <div className="mt-12 flex flex-col sm:flex-row gap-4 justify-center">
          <Button asChild size="lg" data-testid="buy-cta-pumpfun"
            className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/90 font-mono-alt tracking-widest text-xs px-8 py-6">
            <a href="https://pump.fun" target="_blank" rel="noreferrer">{t("howtobuy.cta_pump")}</a>
          </Button>
          <Button asChild size="lg" variant="outline" data-testid="buy-cta-dex"
            className="rounded-sm border-secondary/50 text-secondary hover:bg-secondary/10 hover:text-secondary font-mono-alt tracking-widest text-xs px-8 py-6">
            <a href="https://dexscreener.com" target="_blank" rel="noreferrer">{t("howtobuy.cta_dex")}</a>
          </Button>
        </div>
      </div>
    </section>
  );
}
